/**
 * Sentry Logger
 *
 * Production logger that sends errors to Sentry with sanitized data
 * and proper context for error tracking and debugging
 */

import * as Sentry from '@sentry/react';
import {
  IErrorLogger,
  ErrorContext,
  ApiErrorContext,
  ReactErrorContext,
  ErrorBreadcrumb,
  ContextualError,
  ErrorType,
} from '@shared/types/ErrorLogging';
import {
  DataSanitizer,
  ErrorRateLimit,
  ContextCollector,
} from '@shared/utils/errorUtils';
import { getErrorLoggingConfig } from '@shared/config/errorLogging';

export class SentryLogger implements IErrorLogger {
  private dataSanitizer: DataSanitizer;
  private rateLimit: ErrorRateLimit;
  private config = getErrorLoggingConfig();
  private userContext: { userId?: string; userEmail?: string } = {};

  constructor() {
    this.dataSanitizer = new DataSanitizer();
    this.rateLimit = new ErrorRateLimit(this.config.maxErrorsPerMinute);
  }

  /**
   * Logs an error to Sentry with sanitized context
   */
  logError(
    error: Error | ContextualError,
    context?: Partial<ErrorContext>
  ): void {
    if (!this.rateLimit.shouldLogError(error)) {
      return; // Skip logging due to rate limit
    }

    const enrichedContext = this.enrichContext(context);
    const sanitizedContext = this.dataSanitizer.sanitizeObject(enrichedContext);

    Sentry.withScope(scope => {
      // Set error type and level
      scope.setLevel('error');

      if (error instanceof ContextualError) {
        scope.setTag('error_type', error.context.type);
        scope.setContext(
          'error_context',
          this.dataSanitizer.sanitizeObject(error.context)
        );

        if (error.originalError) {
          scope.setContext('original_error', {
            name: error.originalError.name,
            message: error.originalError.message,
            stack: error.originalError.stack,
          });
        }
      } else {
        scope.setTag('error_type', 'global_error');
      }

      // Add sanitized context
      if (sanitizedContext) {
        Object.entries(sanitizedContext).forEach(([key, value]) => {
          scope.setContext(key, value as any);
        });
      }

      // Capture the error
      Sentry.captureException(error);
    });
  }

  /**
   * Logs a warning to Sentry
   */
  logWarning(message: string, context?: Partial<ErrorContext>): void {
    const enrichedContext = this.enrichContext(context);
    const sanitizedContext = this.dataSanitizer.sanitizeObject(enrichedContext);

    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setTag('log_type', 'warning');

      if (sanitizedContext) {
        Object.entries(sanitizedContext).forEach(([key, value]) => {
          scope.setContext(key, value as any);
        });
      }

      Sentry.captureMessage(message, 'warning');
    });
  }

  /**
   * Logs informational message to Sentry (only in production if enabled)
   */
  logInfo(message: string, context?: Partial<ErrorContext>): void {
    // Only log info messages in production if specifically enabled
    if (this.config.logLevel !== 'info' && this.config.logLevel !== 'debug') {
      return;
    }

    const enrichedContext = this.enrichContext(context);
    const sanitizedContext = this.dataSanitizer.sanitizeObject(enrichedContext);

    Sentry.withScope(scope => {
      scope.setLevel('info');
      scope.setTag('log_type', 'info');

      if (sanitizedContext) {
        Object.entries(sanitizedContext).forEach(([key, value]) => {
          scope.setContext(key, value as any);
        });
      }

      Sentry.captureMessage(message, 'info');
    });
  }

  /**
   * Logs debug information (typically not sent to Sentry in production)
   */
  logDebug(message: string, context?: Partial<ErrorContext>): void {
    // Only log debug messages if explicitly enabled
    if (this.config.logLevel !== 'debug') {
      return;
    }

    const enrichedContext = this.enrichContext(context);
    const sanitizedContext = this.dataSanitizer.sanitizeObject(enrichedContext);

    Sentry.withScope(scope => {
      scope.setLevel('debug');
      scope.setTag('log_type', 'debug');

      if (sanitizedContext) {
        Object.entries(sanitizedContext).forEach(([key, value]) => {
          scope.setContext(key, value as any);
        });
      }

      Sentry.captureMessage(message, 'debug');
    });
  }

  /**
   * Logs API-specific errors with sanitized request/response data
   */
  logApiError(error: Error, apiContext: ApiErrorContext): void {
    if (!this.rateLimit.shouldLogError(error)) {
      return;
    }

    const sanitizedApiContext = {
      ...apiContext,
      type: 'api_error' as ErrorType,
      endpoint: this.dataSanitizer.sanitizeUrl(apiContext.endpoint || ''),
      requestHeaders: this.dataSanitizer.sanitizeHeaders(
        apiContext.requestHeaders || {}
      ),
      responseHeaders: this.dataSanitizer.sanitizeHeaders(
        apiContext.responseHeaders || {}
      ),
      requestBody: this.dataSanitizer.sanitizeObject(apiContext.requestBody),
      responseBody: this.dataSanitizer.sanitizeObject(apiContext.responseBody),
    };

    Sentry.withScope(scope => {
      scope.setLevel('error');
      scope.setTag('error_type', 'api_error');
      scope.setTag('api_method', apiContext.method || 'unknown');
      scope.setTag('api_status', apiContext.status?.toString() || 'unknown');

      scope.setContext('api_request', {
        method: sanitizedApiContext.method,
        endpoint: sanitizedApiContext.endpoint,
        headers: sanitizedApiContext.requestHeaders,
        body: sanitizedApiContext.requestBody,
      });

      scope.setContext('api_response', {
        status: sanitizedApiContext.status,
        statusText: sanitizedApiContext.statusText,
        headers: sanitizedApiContext.responseHeaders,
        body: sanitizedApiContext.responseBody,
        duration: sanitizedApiContext.duration,
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Logs React-specific errors with component context
   */
  logReactError(error: Error, reactContext: ReactErrorContext): void {
    if (!this.rateLimit.shouldLogError(error)) {
      return;
    }

    const sanitizedReactContext = {
      ...reactContext,
      type: 'react_error_boundary' as ErrorType,
      props: this.dataSanitizer.sanitizeObject(reactContext.props),
      state: this.dataSanitizer.sanitizeObject(reactContext.state),
    };

    Sentry.withScope(scope => {
      scope.setLevel('error');
      scope.setTag('error_type', 'react_error_boundary');
      scope.setTag('component_name', reactContext.componentName || 'unknown');
      scope.setTag('error_boundary', reactContext.errorBoundary || 'unknown');

      scope.setContext('react_component', {
        name: sanitizedReactContext.componentName,
        errorBoundary: sanitizedReactContext.errorBoundary,
        componentStack: sanitizedReactContext.componentStack,
        props: sanitizedReactContext.props,
        state: sanitizedReactContext.state,
        hooks: sanitizedReactContext.hooks,
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Adds a breadcrumb to Sentry's breadcrumb trail
   */
  addBreadcrumb(breadcrumb: ErrorBreadcrumb): void {
    const sanitizedData = this.dataSanitizer.sanitizeObject(breadcrumb.data);

    Sentry.addBreadcrumb({
      message: breadcrumb.message,
      category: breadcrumb.category,
      level: breadcrumb.level as any, // Convert our LogLevel to Sentry's level
      timestamp: breadcrumb.timestamp / 1000, // Sentry expects seconds
      data: sanitizedData,
    });
  }

  /**
   * Sets user context in Sentry
   */
  setUserContext(userId: string, userEmail?: string): void {
    if (
      this.userContext.userId === userId &&
      this.userContext.userEmail === userEmail
    ) {
      return;
    }
    this.userContext = { userId, userEmail };
    Sentry.setUser({
      id: userId,
      email: userEmail,
    });
  }

  /**
   * Clears user context in Sentry
   */
  clearUserContext(): void {
    this.userContext = {};
    Sentry.setUser(null);
  }

  /**
   * Enriches context with additional data
   */
  private enrichContext(
    context?: Partial<ErrorContext>
  ): Partial<ErrorContext> {
    const baseContext = ContextCollector.collectErrorContext();
    return {
      ...baseContext,
      ...context,
    };
  }
}
